import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowRight, Play, Sparkles, Check, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import ezyclassLogo from "@/assets/ezyclass-icon.png";
import portalAdminImg from "@/assets/hero-portal-admin-v2.jpg";
import portalTeacherImg from "@/assets/hero-portal-teacher-v2.jpg";
import portalStudentImg from "@/assets/hero-portal-student-v2.jpg";

const EASE = [0.32, 0.72, 0, 1] as const;


export function LandingHero() {
  const { demoSignIn } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  const handleDemo = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await demoSignIn("admin");
      navigate({ to: "/admin" });
    } catch {
      toast.error("Không thể tải demo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative pt-32 pb-12 px-4 overflow-hidden">

      {/* Animated mesh gradient orbs */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50/60 to-white dark:from-slate-950 dark:to-slate-950" />
        <motion.div
          className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full bg-blue-300/30 dark:bg-blue-500/10 blur-[120px]"
          animate={{ x: [0, 40, 0], y: [0, -30, 0], scale: [1, 1.1, 1] }}
          transition={{ repeat: Infinity, duration: 12, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/3 left-1/4 w-[400px] h-[400px] rounded-full bg-indigo-300/25 dark:bg-indigo-500/10 blur-[100px]"
          animate={{ x: [0, -30, 0], y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ repeat: Infinity, duration: 15, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-[350px] h-[350px] rounded-full bg-emerald-300/20 dark:bg-emerald-500/8 blur-[100px]"
          animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
          transition={{ repeat: Infinity, duration: 18, ease: "easeInOut" }}
        />
      </div>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* LEFT — Editorial typography */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, duration: 0.5, ease: EASE }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/10 ring-1 ring-blue-100 dark:ring-blue-400/20 text-[11px] uppercase tracking-[0.15em] font-medium text-blue-700 dark:text-blue-300 mb-6"
          >
            <Sparkles className="size-3.5" strokeWidth={1.5} />
            Từ 41.000đ/tháng — rẻ hơn 1 ly cà phê
          </motion.div>

          <h1 className="font-display text-[clamp(2.5rem,5vw,4rem)] font-bold tracking-tight leading-[1.08]">
            <span className="text-slate-900 dark:text-white">Quản lý trung tâm</span>
            <br />
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-500 bg-clip-text text-transparent">
              gọn gàng, hiệu quả
            </span>
          </h1>

          <p className="mt-6 text-lg text-slate-500 dark:text-slate-400 leading-relaxed max-w-lg">
            Lớp học, điểm danh, học phí, lương giáo viên — tất cả trong một.
            Điểm danh 1 click, giao bài có deadline, báo cáo tự động.
          </p>

          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link
              to="/signup"
              className="group flex items-center gap-2 h-12 pl-6 pr-2 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-medium hover:bg-slate-800 dark:hover:bg-slate-100 dark:bg-slate-800 active:scale-[0.98] transition-all duration-500"
              style={{ transitionTimingFunction: "cubic-bezier(0.32,0.72,0,1)" }}
            >
              Đăng ký miễn phí
              <span className="size-8 rounded-full bg-white/15 dark:bg-slate-900/15 grid place-items-center group-hover:translate-x-0.5 group-hover:scale-105 transition-all duration-500">
                <ArrowRight className="size-4" strokeWidth={1.5} />
              </span>
            </Link>
            <button
              onClick={handleDemo}
              disabled={loading}
              className="flex items-center gap-2 h-12 px-6 rounded-full text-slate-700 dark:text-slate-200 font-medium ring-1 ring-slate-200 dark:ring-slate-800 hover:ring-slate-300 dark:hover:ring-white/25 hover:bg-slate-50 dark:bg-slate-950 dark:hover:bg-white/5 active:scale-[0.98] transition-all duration-500 disabled:opacity-70"
            >
              {loading ? (
                <Loader2 className="size-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Play className="size-4" strokeWidth={1.5} />
              )}
              Xem demo
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-6 text-[13px] text-slate-500 dark:text-slate-400">
            <span className="inline-flex items-center gap-1.5">
              <Check className="size-3.5 text-emerald-600" strokeWidth={2} /> Không cần đăng ký
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="size-3.5 text-emerald-600" strokeWidth={2} /> Cài đặt 2 phút
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="size-3.5 text-emerald-600" strokeWidth={2} /> Dữ liệu mẫu sẵn
            </span>
          </div>
        </motion.div>

        {/* RIGHT — Orbital Hub */}
        <div className="relative hidden lg:flex items-center justify-center min-h-[560px]">
          {/* Connector lines */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 500 560"
            fill="none"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="line-blue" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#6366f1" stopOpacity="0.1" />
              </linearGradient>
              <linearGradient id="line-emerald" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0.1" />
              </linearGradient>
            </defs>
            <motion.line x1="250" y1="280" x2="110" y2="120" stroke="url(#line-blue)" strokeWidth="1.5" strokeDasharray="6 6"
              initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 1.2, delay: 0.4, ease: EASE }} />
            <motion.line x1="250" y1="280" x2="400" y2="130" stroke="url(#line-blue)" strokeWidth="1.5" strokeDasharray="6 6"
              initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 1.2, delay: 0.55, ease: EASE }} />
            <motion.line x1="250" y1="280" x2="410" y2="400" stroke="url(#line-emerald)" strokeWidth="1.5" strokeDasharray="6 6"
              initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 1.2, delay: 0.7, ease: EASE }} />
            <motion.line x1="250" y1="280" x2="150" y2="450" stroke="url(#line-emerald)" strokeWidth="1.5" strokeDasharray="6 6"
              initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 1.2, delay: 0.85, ease: EASE }} />
          </svg>

          {/* CENTER — Ezyclass logo + glow pulse */}
          <motion.div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 18, delay: 0.2 }}
          >
            <motion.div
              className="absolute inset-0 rounded-[1.75rem] bg-blue-500/30 blur-2xl"
              animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.7, 0.4] }}
              transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
            />
            <div className="relative p-1.5 rounded-[1.75rem] bg-white/80 dark:bg-white/10 backdrop-blur-xl ring-1 ring-black/[0.06] dark:ring-white/10 shadow-[0_16px_50px_rgb(59,130,246,0.25)]">
              <div className="size-20 rounded-[calc(1.75rem-6px)] bg-white grid place-items-center">
                <img src={ezyclassLogo} alt="Ezyclass" className="size-16 object-contain" />
              </div>
            </div>
          </motion.div>

          {/* NODE 1 — Admin top-left */}
          <FloatNode className="absolute left-[10%] top-[12%]" delay={0.5} floatDur={4}>
            <NodeCard img={portalAdminImg} label="Quản lý trung tâm" dotColor="bg-blue-500" gradient="from-blue-500 to-indigo-600" />
          </FloatNode>

          {/* NODE 2 — Teacher top-right */}
          <FloatNode className="absolute right-[6%] top-[16%]" delay={0.65} floatDur={4.5}>
            <NodeCard img={portalTeacherImg} label="Giáo viên" dotColor="bg-indigo-500" gradient="from-indigo-500 to-violet-600" />
          </FloatNode>

          {/* NODE 3 — Notification card right */}
          <FloatNode className="absolute right-[4%] top-[52%]" delay={0.8} floatDur={5}>
            <div className="p-1 rounded-2xl bg-white/80 dark:bg-white/5 backdrop-blur-lg ring-1 ring-black/[0.05] dark:ring-white/10 shadow-[0_12px_40px_rgb(0,0,0,0.08)]">
              <div className="rounded-[calc(1rem-4px)] bg-white dark:bg-slate-900 px-4 py-3 flex items-center gap-2.5">
                <div className="size-8 rounded-full bg-emerald-100 dark:bg-emerald-500/15 grid place-items-center shrink-0">
                  <Check className="size-4 text-emerald-600" strokeWidth={2} />
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-slate-900 dark:text-white">Điểm danh hoàn tất</div>
                  <div className="text-[10px] text-slate-400 dark:text-slate-500">Lớp 10A · 28/30 có mặt</div>
                </div>
              </div>
            </div>
          </FloatNode>

          {/* NODE 4 — Student/Parent bottom-left */}
          <FloatNode className="absolute left-[14%] bottom-[8%]" delay={0.95} floatDur={4.2}>
            <NodeCard img={portalStudentImg} label="Phụ huynh & Học sinh" dotColor="bg-emerald-500" gradient="from-emerald-500 to-teal-600" />
          </FloatNode>


          {/* Extra floating — payment */}
          <FloatNode className="absolute right-[22%] bottom-[20%] z-10" delay={1.1} floatDur={5.5}>
            <div className="p-1 rounded-2xl bg-white/80 dark:bg-white/5 backdrop-blur-lg ring-1 ring-black/[0.05] dark:ring-white/10 shadow-lg">
              <div className="rounded-[calc(1rem-4px)] bg-white dark:bg-slate-900 px-3.5 py-2.5">
                <div className="text-[9px] uppercase tracking-wider text-slate-400 dark:text-slate-500">Thanh toán</div>
                <div className="text-base font-bold text-emerald-600">+3.500.000đ</div>
                <div className="text-[9px] text-slate-400 dark:text-slate-500">Học phí tháng 6</div>
              </div>
            </div>
          </FloatNode>
        </div>
      </div>
    </section>
  );
}

function FloatNode({
  children,
  className,
  delay,
  floatDur,
}: {
  children: React.ReactNode;
  className?: string;
  delay: number;
  floatDur: number;
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, scale: 0.8, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 180, damping: 16, delay }}
    >
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ repeat: Infinity, duration: floatDur, ease: "easeInOut" }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

function NodeCard({ img, label, dotColor, gradient }: { img: string; label: string; dotColor: string; gradient: string }) {
  const [imgError, setImgError] = React.useState(false);
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="p-1 rounded-full bg-white/70 dark:bg-white/5 backdrop-blur-lg ring-1 ring-black/[0.06] dark:ring-white/10 shadow-[0_12px_40px_rgb(0,0,0,0.1)]">
        <div className={`size-20 rounded-full overflow-hidden ${imgError ? `bg-gradient-to-br ${gradient}` : ""}`}>
          {!imgError && (
            <img
              src={img}
              alt={label}
              loading="lazy"
              width={80}
              height={80}
              className="size-full object-cover"
              onError={() => setImgError(true)}
            />
          )}
        </div>
      </div>
      <div className="px-3 py-1.5 rounded-full bg-white/80 dark:bg-slate-900/80 backdrop-blur-md ring-1 ring-black/[0.05] dark:ring-white/10 shadow-sm flex items-center gap-1.5">
        <span className={`size-1.5 rounded-full ${dotColor}`} />
        <span className="text-[11px] font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap">{label}</span>
      </div>
    </div>
  );
}

